import { makeObservable, observable, action } from 'mobx';
import { LocalStorageService } from './LocalStorageService';
import { STORAGE_KEYS } from '../../constants/constants';
import { logger } from 'src/utils/logger';

/**
 * 视图管理服务
 * 负责视图选择、持久化和监听器管理
 */
export class ViewManagementService {
  constructor(storageService = new LocalStorageService()) {
    this._selectedViewId = null;
    this._viewChangeListeners = [];
    this._storageService = storageService;

    makeObservable(this, {
      _selectedViewId: observable,
      _viewChangeListeners: observable,
      setSelectedViewId: action,
      addViewChangeListener: action,
      removeViewChangeListener: action,
    });
  }

  get selectedViewId() {
    return this._selectedViewId;
  }

  /**
   * 设置选中的视图ID
   * @param {string} viewId - 视图ID
   */
  setSelectedViewId(viewId) {
    const oldViewId = this._selectedViewId;
    this._selectedViewId = viewId;

    // 保存到存储
    if (viewId) {
      this._storageService.save(STORAGE_KEYS.LAST_SELECTED_VIEW, viewId);
      logger.debug('[ViewManagementService] 保存上次访问的视图ID:', viewId);
    }

    // 触发视图变化监听器
    if (oldViewId !== viewId) {
      this._notifyViewChangeListeners(viewId, oldViewId);
    }
  }

  /**
   * 从存储恢复上次访问的视图ID
   * @returns {string|null} 视图ID
   */
  restoreLastSelectedView() {
    try {
      const lastSelectedViewId = this._storageService.get(STORAGE_KEYS.LAST_SELECTED_VIEW);
      logger.debug('[ViewManagementService] 尝试恢复视图选择:', lastSelectedViewId);
      return lastSelectedViewId;
    } catch (error) {
      logger.error('[ViewManagementService] 恢复视图选择失败:', error);
      this._storageService.remove(STORAGE_KEYS.LAST_SELECTED_VIEW);
      return null;
    }
  }

  /**
   * 检查视图是否存在于当前表数据中
   * @param {string} viewId - 视图ID
   * @param {Array} views - 视图列表
   * @returns {boolean} 是否存在
   */
  isViewExists(viewId, views) {
    if (!views || !Array.isArray(views)) {
      return false;
    }
    return views.some(view => view.id === viewId);
  }

  /**
   * 添加视图变化监听器
   * @param {Function} listener - 监听器函数
   */
  addViewChangeListener(listener) {
    this._viewChangeListeners.push(listener);
  }

  /**
   * 移除视图变化监听器
   * @param {Function} listener - 监听器函数
   */
  removeViewChangeListener(listener) {
    this._viewChangeListeners = this._viewChangeListeners.filter(l => l !== listener);
  }

  /**
   * 通知视图变化监听器
   * @param {string} newViewId - 新视图ID
   * @param {string} oldViewId - 旧视图ID
   */
  _notifyViewChangeListeners(newViewId, oldViewId) {
    this._viewChangeListeners.forEach(listener => {
      try {
        listener(newViewId, oldViewId);
      } catch (error) {
        logger.error('[ViewManagementService] 视图变化监听器执行失败:', error);
      }
    });
  }
}
